import React, { useEffect, useState } from 'react';
import { indexedDBService, SavedList } from '@/lib/indexedDBService';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { FileDown, Trash2, Eye, BarChart, X, FileText } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { Progress } from '@/components/ui/progress';
import { generateCutListPDF } from '@/utils/cutListPdf';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface SavedListsViewProps {
  onLoadList: (list: SavedList) => void;
}

const SavedListsView: React.FC<SavedListsViewProps> = ({ onLoadList }) => {
  const [savedLists, setSavedLists] = useState<SavedList[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [expandedCardId, setExpandedCardId] = useState<string | null>(null);

  useEffect(() => {
    loadSavedLists();
  }, []);

  const loadSavedLists = async () => {
    setIsLoading(true);
    try {
      const lists = await indexedDBService.getSavedLists();
      setSavedLists(lists);
    } catch (error) {
      console.error('Error loading saved lists:', error);
      toast({
        title: "Error",
        description: "Failed to load saved lists.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      await indexedDBService.deleteSavedList(deleteId);
      setSavedLists(savedLists.filter(list => list.id !== deleteId));
      toast({
        title: "List Deleted",
        description: "The list has been deleted successfully.",
      });
    } catch (error) {
      console.error('Error deleting list:', error);
      toast({
        title: "Delete Failed",
        description: "There was an error deleting the list.",
        variant: "destructive",
      });
    } finally {
      setDeleteId(null);
    }
  };

  const handleExport = (list: SavedList) => {
    try {
      // Generate PDF
      const doc = generateCutListPDF(list);

      // Save the PDF
      const filename = `${list.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${new Date().toLocaleDateString().replace(/\//g, '-')}.pdf`;
      doc.save(filename);

      toast({
        title: "List Exported",
        description: "The list has been exported as a PDF file.",
      });
    } catch (error) {
      console.error('Error exporting list as PDF:', error);
      toast({
        title: "Export Failed",
        description: "There was an error exporting the list as PDF.",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center p-8">
        <Progress value={80} className="w-64 h-2 mb-4" />
        <p className="text-muted-foreground">Loading saved lists...</p>
      </div>
    );
  }

  if (savedLists.length === 0) {
    return (
      <div className="text-center p-8 border border-dashed rounded-lg">
        <p className="text-muted-foreground mb-2">No saved lists found.</p>
        <p className="text-sm text-muted-foreground">Generate a cut list and save it to see it here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {savedLists.map(list => (
          <Card key={list.id} className="overflow-hidden">
            {expandedCardId === list.id ? (
              <>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-lg">{list.name}</CardTitle>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setExpandedCardId(null)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </CardHeader>
                <CardContent className="pb-2">
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-sm font-medium mb-1">Summary</h4>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <p className="text-muted-foreground">Materials</p>
                          <p>{list.materials.length}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Stock Pieces</p>
                          <p>{list.result.totalStock}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Utilization</p>
                          <p>{list.result.overallUtilization.toFixed(1)}%</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Date</p>
                          <p>{new Date(list.date).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-sm font-medium mb-1">Materials</h4>
                      <div className="max-h-40 overflow-y-auto">
                        {list.materials.map((item, index) => (
                          <div key={index} className="text-sm flex justify-between py-1 border-b border-border last:border-0">
                            <span>{item.name}</span>
                            <span>{item.length}" × {item.quantity}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>

                <CardFooter className="flex justify-end space-x-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onLoadList(list)}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Load
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleExport(list)}
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Export PDF
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDeleteId(list.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </CardFooter>
              </>
            ) : (
              <>
                <CardContent>
                  <div className="text-sm space-y-2 mb-4">
                    <p>Materials: {list.materials.length}</p>
                    <p>Stock Pieces: {list.result.totalStock}</p>
                    <p>Utilization: {list.result.overallUtilization.toFixed(1)}%</p>
                  </div>
                  <div className="flex justify-end space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setExpandedCardId(list.id)}
                    >
                      <BarChart className="h-4 w-4 mr-2" />
                      Preview
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onLoadList(list)}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      Load
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleExport(list)}
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Export PDF
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDeleteId(list.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </>
            )}
          </Card>
        ))}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete List</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this list? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SavedListsView;
